import React, { useCallback, useState } from 'react';
import { Input } from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { usePasswordlessAuth } from '../../hooks/usePasswordlessAuth';
import { SignInFrame } from './SignInFrame';
import { PasswordlessFrameProps } from './PasswordlessSignIn';

type PasswordlessRequestFormValues = {
  signInEmail: string;
};

const VALIDATIONS = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

type PasswordlessRequestStepProps = PasswordlessFrameProps & {
  defaultEmail?: string;
  disableEmail?: boolean;
  message?: string;
  onRequested: (email: string) => void;
  showCancelButton?: boolean;
};

export const PasswordlessRequestStep: React.FC<PasswordlessRequestStepProps> = ({
  className,
  defaultEmail,
  disableEmail = false,
  hideAuthModal,
  message = "Enter your email and we'll send you a code to sign in.",
  onAuth,
  onRequested,
  showAuthModal,
  showSocialAuth = true,
  showCancelButton = true,
  title
}) => {
  const { clearError, error, loading, requestLogin } = usePasswordlessAuth();
  const [email, setEmail] = useState(defaultEmail || '');

  const handleEmailChange = useCallback(
    (value: string | number) => {
      if (error) clearError();
      setEmail(String(value));
    },
    [clearError, error]
  );

  const handleRequestSubmit = useCallback(
    (values: PasswordlessRequestFormValues) => {
      const trimmedEmail = values.signInEmail.trim();

      requestLogin({ email: trimmedEmail })
        .then(() => {
          onRequested(trimmedEmail);
        })
        .catch(noop);
    },
    [onRequested, requestLogin]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit: handleRequestSubmit,
    validations: VALIDATIONS
  });

  return (
    <SignInFrame
      className={className}
      error={error?.message}
      hideAuthModal={hideAuthModal}
      loading={loading}
      message={message}
      onAuth={onAuth}
      onSubmit={handleSubmit}
      showAuthModal={showAuthModal}
      showSocialAuth={showSocialAuth}
      showCancelButton={showCancelButton}
      submitLabel="CONTINUE"
      title={title}
    >
      <Input
        className="Authentication__input"
        disabled={disableEmail}
        error={fieldErrors.signInEmail?.message || error != null}
        id="signInEmail"
        data-testid="signInEmail"
        onChange={handleEmailChange}
        placeholder="Email"
        type="email"
        value={email}
      />
    </SignInFrame>
  );
};
